import React from "react";
import { useThemeMode } from "../../../context/contextTheme";
import { useChat } from "../../../context/contextChat"; // Ensure path points to your context hooks
import hexToRGBA from "../../../utils/hexToRGBA";

// Standard formatting utility for time representation
const convertToRelativeTime = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const StatItem = ({ label, value, color }) => (
  <div className="px-3 text-center">
    <div className="small opacity-75 mb-0" style={{ fontSize: "0.75rem" }}>{label}</div>
    <div className="fw-bold fs-5" style={{ color: color || '#fff' }}>{value ?? 0}</div>
  </div>
);

/* ==========================================================================
   FILE COMPONENT REFACTOR (Data Bound to your scan_data Model)
   ========================================================================== */
const FilePopup = ({ data, onAiTrigger }) => {
  const { theme } = useThemeMode();

  const scanDataObj = data?.scan_data || {};
  const attr = scanDataObj?.data?.attributes || scanDataObj?.attributes || {};
  const meta = scanDataObj?.meta || {};
  const fileSize = meta?.file_info?.size || 0;

  const stats = data?.status || attr?.stats || {};
  const results = attr?.results || {};

  return (
    <div className="d-flex flex-column gap-4">
      {/* File Header Block */}
      <div>
        <div className="card border-0 rounded-4 shadow-sm text-white overflow-hidden mb-3" style={{ background: '#344266' }}>
          <div className="card-body p-4">
            <div className="row align-items-center">
              <div className="col-md-5 border-end border-white border-opacity-10">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-10 p-3 rounded-3 me-3">
                    <i className="bi bi-file-earmark-binary fs-3"></i>
                  </div>
                  <div className="overflow-hidden flex-grow-1">
                    <h6 className="mb-1 opacity-75 small">File Analysis Report</h6>
                    <div className="fw-bold text-truncate mb-0" style={{ fontSize: "1.05rem" }} title={data.dir}>
                      {data.dir?.split('/').pop() || "malware-file.php"}
                    </div>
                    <div className="small opacity-50" style={{ fontSize: "0.75rem" }}>
                      Size: {fileSize ? `${(fileSize / 1024).toFixed(2)} KB` : "Unknown Size"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-7 mt-3 mt-md-0">
                <div className="d-flex justify-content-around">
                  <StatItem label="Malicious" value={stats.malicious} color="#ff4d4d" />
                  <StatItem label="Suspicious" value={stats.suspicious} color="#ffc107" />
                  <StatItem label="Harmless" value={stats.harmless} color="#00a73d" />
                  <StatItem label="Undetected" value={stats.undetected} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Action Control (Placed right below file metadata info card) */}
        <div className="d-flex justify-content-end px-1">
          <button
            className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-2 border-0 rounded-3 py-2 px-3 animate-pulse-ai"
            style={{ fontSize: '0.8rem', background: '#344266', fontWeight: 600, minWidth: "160px" }}
            onClick={onAiTrigger}
          >
            <i className="bi bi-stars fs-6 text-warning"></i> Ask AI Assistant
          </button>
        </div>
      </div>

      {/* File Detection Table */}
      <div>
        <h6 className="fw-bold mb-2 ps-1" style={{ color: theme.textColor, fontSize: "0.85rem" }}>SECURITY ENGINE SIGNATURE ANALYSIS</h6>
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden" style={{ backgroundColor: theme.labelBgMuted || "#f8fafc" }}>
          <div className="table-responsive" style={{ maxHeight: "300px" }}>
            <table className="table table-hover align-middle mb-0" style={{ color: theme.textColor }}>
              <thead className="small text-muted" style={{ position: "sticky", top: 0, backgroundColor: theme.labelBgMuted || "#f8fafc", zIndex: 2 }}>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <th className="ps-4 py-2.5">SECURITY ENGINE</th>
                  <th className="py-2.5">METHOD</th>
                  <th className="py-2.5">CATEGORY</th>
                  <th className="pe-4 py-2.5 text-end">RESULT</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(results).length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted p-4 small">
                      <i className="bi bi-exclamation-circle me-1"></i> No individual scanner engine records populated in payload.
                    </td>
                  </tr>
                ) : (
                  Object.keys(results).map((key) => {
                    const r = results[key];
                    const isClean = r.result === 'clean' || r.result === 'undetected' || !r.result;
                    const isMalicious = r.result === 'malicious' || r.result === 'phishing' || r.result === 'suspicious';

                    const style = isClean
                      ? { bg: hexToRGBA('#00a73d', 0.1), text: '#00a73d', icon: 'shield-check' }
                      : isMalicious
                        ? { bg: hexToRGBA('#dc3545', 0.1), text: '#dc3545', icon: 'bug' }
                        : { bg: hexToRGBA(theme.textMuted || "#64748b", 0.1), text: theme.textMuted, icon: 'question-circle' };

                    return (
                      <tr key={key} style={{ borderBottom: `1px solid ${hexToRGBA(theme.border, 0.2)}` }}>
                        <td className="ps-4 py-2.5 fw-bold" style={{ fontSize: "0.85rem" }}>{r.engine_name || key}</td>
                        <td className="py-2.5 text-muted small">{r.method || "blacklist match"}</td>
                        <td className="py-2.5 text-muted small">{r.category || 'malware'}</td>
                        <td className="pe-4 py-2.5 text-end">
                          <span className="badge rounded-pill px-2.5 py-1.5" style={{ backgroundColor: style.bg, color: style.text, fontSize: "0.7rem" }}>
                            <i className={`bi bi-${style.icon} me-1`}></i>
                            {(r.result || 'undetected').toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* File Hash Metadata Cards */}
      <div className="row g-3">
        {[
          { title: "Crypto Hashes", icon: "hash", items: { "SHA-256": attr.sha256 || data.scan_uuid, "MD5": attr.md5 || "N/A", "Type/Magic": attr.magic || "PHP script text/payload" } },
          { title: "Scan Timeline", icon: "clock-history", items: { "First Logged": attr.first_submission_date ? new Date(attr.first_submission_date * 1000).toLocaleString() : convertToRelativeTime(data.scan_time), "Last Logged": attr.last_submission_date ? new Date(attr.last_submission_date * 1000).toLocaleString() : convertToRelativeTime(data.scan_time) } },
          { title: "Community Feedback", icon: "people", items: { "Type Unsupported Count": stats["type-unsupported"] ?? 0, "Failure Counts": stats.failure ?? 0 } }
        ].map((card, idx) => (
          <div className="col-lg-4" key={idx}>
            <div className="card h-100 border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted || "#f8fafc" }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center mb-2.5">
                  <div className="p-1.5 rounded-2 me-2 d-flex align-items-center" style={{ background: hexToRGBA('#344266', 0.1), color: '#344266' }}>
                    <i className={`bi bi-${card.icon}`}></i>
                  </div>
                  <h6 className="fw-bold mb-0" style={{ color: theme.textColor, fontSize: "0.85rem" }}>{card.title}</h6>
                </div>
                <div className="small">
                  {Object.entries(card.items).map(([k, v]) => (
                    <div key={k} className="mb-2 d-flex flex-column">
                      <span className="text-muted text-uppercase font-monospace" style={{ fontSize: '0.6rem', fontWeight: 700 }}>{k}</span>
                      <span className="text-break fw-medium" style={{ color: theme.textColor, fontSize: "0.78rem" }}>{v ?? 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==========================================================================
   URL COMPONENT REFACTOR (Data Bound to your scan_data Model)
   ========================================================================== */
const UrlPopup = ({ data, onAiTrigger }) => {
  const { theme } = useThemeMode();

  const scanDataObj = data?.scan_data || {};
  const attr = scanDataObj?.data?.attributes || scanDataObj?.attributes || {};

  const url = attr?.url || data.dir || "N/A";
  const status_code = attr?.last_http_response_code || 200;
  const content_type = attr?.last_http_response_headers?.["Content-Type"] || "text/html";
  const date_of_last_scan = convertToRelativeTime(data.scan_time);

  const results = attr?.last_analysis_results || attr?.results || {};
  const stats = data?.status || attr?.total_votes || {};

  return (
    <div className="d-flex flex-column gap-4">
      {/* URL Header Block */}
      <div>
        <div className="card border-0 rounded-4 p-4 shadow-sm text-white mb-3" style={{ background: '#344266' }}>
          <div className="row align-items-center">
            <div className="col-lg-6 mb-3 mb-lg-0 overflow-hidden">
              <div className="small opacity-75 mb-1" style={{ fontSize: "0.75rem" }}>Scanned Domain Network Location</div>
              <h5 className="text-truncate fw-bold mb-0" style={{ fontSize: "1.05rem" }} title={url}>
                <i className="bi bi-globe2 me-2" />{url}
              </h5>
            </div>
            <div className="col-lg-6">
              <div className="d-flex justify-content-lg-end gap-3 text-center">
                <div>
                  <div className="small opacity-75 mb-0.5" style={{ fontSize: "0.7rem" }}>HTTP status</div>
                  <div className="fw-bold small d-flex align-items-center justify-content-center">
                    <i className="bi bi-check-circle text-success me-1" />
                    {status_code}
                  </div>
                </div>
                <div className="vr opacity-25"></div>
                <div>
                  <div className="small opacity-75 mb-0.5" style={{ fontSize: "0.7rem" }}>Mime-Type</div>
                  <div className="fw-bold small text-truncate" style={{ maxWidth: "120px" }}>{content_type.split(';')[0]}</div>
                </div>
                <div className="vr opacity-25"></div>
                <div>
                  <div className="small opacity-75 mb-0.5" style={{ fontSize: "0.7rem" }}>Execution Timestamp</div>
                  <div className="fw-bold small">{date_of_last_scan}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Action Control (Placed right below domain network details card) */}
        <div className="d-flex justify-content-end px-1">
          <button
            className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-2 border-0 rounded-3 py-2 px-3 animate-pulse-ai"
            style={{ fontSize: '0.8rem', background: '#344266', fontWeight: 600, minWidth: "160px" }}
            onClick={onAiTrigger}
          >
            <i className="bi bi-stars fs-6 text-warning"></i> Ask AI Assistant
          </button>
        </div>
      </div>

      {/* URL Detection Table */}
      <div>
        <h6 className="fw-bold mb-2 ps-1" style={{ color: theme.textColor, fontSize: "0.85rem" }}>GLOBAL THREAT INTERACTION FEED</h6>
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden" style={{ backgroundColor: theme.labelBgMuted || "#f8fafc" }}>
          <div className="table-responsive" style={{ maxHeight: "300px" }}>
            <table className="table table-borderless align-middle mb-0" style={{ color: theme.textColor }}>
              <thead className="text-muted small" style={{ position: "sticky", top: 0, backgroundColor: theme.labelBgMuted || "#f8fafc", zIndex: 2 }}>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <th className="pb-2.5 ps-4">ENGINE NAME</th>
                  <th className="pb-2.5 text-center">CATEGORY</th>
                  <th className="pb-2.5 pe-4 text-end">RESULT</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(results).length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center text-muted p-4 small">
                      <i className="bi bi-shield-check text-success me-1"></i> Checked domains cleared clean across active watchlists.
                    </td>
                  </tr>
                ) : (
                  Object.keys(results).map((key) => {
                    const r = results[key];
                    const isClean = r.result === 'clean' || r.result === 'unrated';
                    return (
                      <tr key={key} style={{ borderBottom: `1px solid ${hexToRGBA(theme.border, 0.2)}` }}>
                        <td className="py-2.5 ps-4 fw-bold" style={{ fontSize: "0.85rem" }}>{r.engine_name || key}</td>
                        <td className="py-2.5 text-center text-muted small">{r.category || "phishing check"}</td>
                        <td className="py-2.5 pe-4 text-end">
                          <span className="badge rounded-pill px-2.5 py-1.5"
                            style={{
                              backgroundColor: isClean ? hexToRGBA('#00a73d', 0.1) : hexToRGBA('#dc3545', 0.1),
                              color: isClean ? '#00a73d' : '#dc3545',
                              fontSize: '0.7rem'
                            }}>
                            <i className={`bi bi-${isClean ? 'shield-check' : 'bug'} me-1`} />
                            {(r.result || 'clean').toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* URL Detailed Analytics Cards */}
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card h-100 border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted || "#f8fafc" }}>
            <div className="card-body p-3">
              <h6 className="fw-bold mb-2.5 d-flex align-items-center" style={{ color: theme.textColor, fontSize: "0.85rem" }}>
                <i className="bi bi-file-earmark-code me-2 text-primary" />HTTP Metadata
              </h6>
              <div className="small">
                <div className="d-flex justify-content-between mb-1.5"><span>Length:</span> <b>{attr.last_http_response_content_length || 0} bytes</b></div>
                <div className="text-break">
                  <span className="text-muted text-uppercase" style={{ fontSize: "0.6rem" }}>Scan UUID Target:</span><br />
                  <b className="text-muted small font-monospace">{data.scan_uuid}</b>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card h-100 border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted || "#f8fafc" }}>
            <div className="card-body p-3">
              <h6 className="fw-bold mb-2.5 d-flex align-items-center" style={{ color: theme.textColor, fontSize: "0.85rem" }}>
                <i className="bi bi-hand-thumbs-up me-2 text-success" />Aggregates
              </h6>
              <div className="small">
                <div className="d-flex justify-content-between mb-1.5 text-success"><span>Harmless:</span> <b>{stats.harmless ?? 0}</b></div>
                <div className="d-flex justify-content-between text-danger"><span>Malicious:</span> <b>{stats.malicious ?? 0}</b></div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card h-100 border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted || "#f8fafc" }}>
            <div className="card-body p-3">
              <h6 className="fw-bold mb-2.5 d-flex align-items-center" style={{ color: theme.textColor, fontSize: "0.85rem" }}>
                <i className="bi bi-tags me-2 text-warning" />Classifications
              </h6>
              <div className="small" style={{ maxHeight: "75px", overflowY: "auto" }}>
                {Object.entries(attr.categories || {}).length === 0 ? <span className="text-muted small">No external tags</span> :
                  Object.entries(attr.categories || {}).map(([key, val]) => (
                    <div key={key} className="mb-1 d-flex justify-content-between text-truncate">
                      <span className="text-muted me-1 small">{key}:</span> <b className="small">{val}</b>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================================================
   MAIN DYNAMIC ROUTER WINDOW MODAL
   ========================================================================== */
const ScanDetailModal = ({ activeAsset, onClose }) => {
  const { theme } = useThemeMode();

  /* ---------------- CONNECT CHAT CONTEXT STATE TRIGGERS ---------------- */
  const { setPrompt, setShowChat, checkPrompt } = useChat();

  if (!activeAsset) return null;

  const isFileType = activeAsset.asset_type === "file";

  // Shared orchestrator to format prompt contexts and open AI side views
  const handleExecuteAiAssist = () => {
    const targetName = activeAsset.dir || "Unknown Context Entry";
    const scanDataObj = activeAsset.scan_data || {};
    const attr = scanDataObj?.data?.attributes || scanDataObj?.attributes || {};

    const maliciousCount = activeAsset.status?.malicious || 0;
    const harmlessCount = activeAsset.status?.harmless || 0;
    const maliciousResult = maliciousCount > 0 ? "MALICIOUS" : "CLEAN";

    let payloadPrompt = "";

    if (isFileType) {
      payloadPrompt = `Analyze this security scan file asset "${targetName.split('/').pop()}". Category indicators: Malicious=${maliciousCount}, Harmless=${harmlessCount}. The global execution scan returned a result of: ${maliciousResult}. Provide a definitive technical remediation guide explaining threat implications and protective containment actions.`;
    } else {
      const urlTarget = attr?.url || targetName;
      payloadPrompt = `Analyze this security threat vectors endpoint for URL "${urlTarget}". Category indicators: Malicious=${maliciousCount}, Harmless=${harmlessCount}. The analysis scan returned an overall status result of: ${maliciousResult}. Review phishing risk behaviors and construct a firewall containment outline.`;
    }

    // Pass information forward to Chat System context hook triggers
    setPrompt(payloadPrompt);
    setShowChat(true);
    if (typeof checkPrompt === "function") {
      checkPrompt(payloadPrompt);
    }

    // Auto-dismiss current modal details panel so the workspace transitions to the chat view cleanly
    onClose();
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        zIndex: 1100,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(8px)",
        animation: "fadeInModal 0.2s cubic-bezier(0.16, 1, 0.3, 1)"
      }}
      onClick={onClose}
    >
      <div
        className="card border-0 p-4 w-100 mx-3 position-relative style-modal-scroll"
        style={{
          maxWidth: "920px",
          maxHeight: "85vh",
          overflowY: "auto",
          backgroundColor: theme.background,
          color: theme.textColor,
          borderRadius: "20px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button Trigger */}
        <button
          type="button"
          className="btn-close position-absolute"
          style={{
            top: "24px",
            right: "24px",
            filter: theme.background === "#ffffff" ? "none" : "invert(1)",
            zIndex: 10
          }}
          onClick={onClose}
          aria-label="Close"
        />

        {/* Component Router Gate */}
        <div className="pt-2">
          {isFileType ? (
            <FilePopup data={activeAsset} onAiTrigger={handleExecuteAiAssist} />
          ) : (
            <UrlPopup data={activeAsset} onAiTrigger={handleExecuteAiAssist} />
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInModal {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .style-modal-scroll::-webkit-scrollbar { width: 6px; }
        .style-modal-scroll::-webkit-scrollbar-track { background: transparent; }
        .style-modal-scroll::-webkit-scrollbar-thumb { background: ${hexToRGBA(theme.textColor, 0.15)}; border-radius: 10px; }
        
        @keyframes subtle-pulse {
          0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
          70% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0); }
          100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
        }
        .animate-pulse-ai {
          animation: subtle-pulse 2s infinite;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .animate-pulse-ai:hover {
          transform: translateY(-1px);
          background-color: #6366f1 !important;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35) !important;
        }
      `}</style>
    </div>
  );
};

export default ScanDetailModal;